// ---------------------------------------------------------------------------
// Talking to the shop's own print agent, on this computer, with no server.
//
// The till normally posts its rasters to the API, which records the job and
// - in the cloud deployment - leaves it for the shop's print agent to send.
// With the line down the API is out of reach and the printer is not, so this
// is the second door: the same raster, handed straight to the agent over
// 127.0.0.1. The agent prints it, writes it down, and carries the record into
// the cloud when the cloud is back. The till never sends the same document
// through both doors: this one is used only while the server is unreachable.
//
// Chromium treats a page on a public HTTPS origin reaching a loopback
// address as "local network access": the request is marked for it and the
// browser may ask the cashier once to allow it. On the shop PC that is the
// whole permission story; nothing is disabled globally.
// ---------------------------------------------------------------------------

import type { RasterPayload } from '../utils/rasterise';

/// Where the agent listens. One address, configurable at build time for a
/// shop that runs it on another port; never a secret.
export const PRINT_AGENT_URL: string =
  (import.meta.env.VITE_PRINT_AGENT_URL as string | undefined)?.replace(/\/$/, '') ?? 'http://127.0.0.1:4010';

/// The agent answers slowly only when a printer does: the LAN transport waits
/// up to fifteen seconds for a printer that has gone quiet.
const AGENT_TIMEOUT_MS = 20_000;

export interface AgentPrintInput {
  /// The till's own id for the document: stable across retries, so the agent
  /// can refuse to print it twice.
  key: string;
  documentType: 'CASHIER_RECEIPT' | 'KITCHEN_TICKET';
  printerRole: 'CASHIER_RECEIPT' | 'KITCHEN_TICKET';
  /// Null for a receipt means the shop's chosen receipt printer.
  printerProfileId: string | null;
  raster: RasterPayload;
}

export interface AgentPrintResult {
  status: 'SENT' | 'FAILED';
  error: string | null;
  /// True when the agent had already printed this document and sent nothing.
  replayed: boolean;
  printerName: string | null;
}

export async function printViaAgent(input: AgentPrintInput): Promise<AgentPrintResult> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeout = controller ? setTimeout(() => controller.abort(), AGENT_TIMEOUT_MS) : null;
  try {
    const response = await fetch(`${PRINT_AGENT_URL}/api/agent/print`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
      // No cookies travel to the agent; it has no session to check.
      credentials: 'omit',
      ...(controller ? { signal: controller.signal } : {}),
      // Chromium's local-network-access marker, ignored by browsers that do
      // not know it. Typed loosely because the DOM types do not carry it yet.
      ...({ targetAddressSpace: 'loopback' } as Record<string, unknown>),
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
      return {
        status: 'FAILED',
        error: body?.error?.message ?? `The shop print agent answered ${response.status}.`,
        replayed: false,
        printerName: null,
      };
    }
    return (await response.json()) as AgentPrintResult;
  } catch {
    return {
      status: 'FAILED',
      error: 'The shop print agent could not be reached on this computer.',
      replayed: false,
      printerName: null,
    };
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}
